import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, Mail, FileText, Layers, Sparkles, ArrowRight, X, AlertCircle, Loader2 } from 'lucide-react';
import { getPlans, formatPrice, initiatePayment, type Plan, type InitiatePaymentRequest } from '../../api/payment';
import { useAuth } from '../../contexts/AuthContext';
import PayTRIframe from '../subscription/PayTRIframe';

interface UpgradePromptProps {
  requiredPlan?: 'mail' | 'template' | 'bundle';
}

const UpgradePrompt: React.FC<UpgradePromptProps> = ({ requiredPlan }) => {
  const { refreshAuth } = useAuth();
  const [plans, setPlans] = useState<{
    mail: Plan[];
    template: Plan[];
    bundle: Plan[];
  } | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('yearly');
  const [selectedModule, setSelectedModule] = useState<'mail' | 'template' | 'bundle'>(requiredPlan || 'bundle');
  const [loading, setLoading] = useState(true);
  const [isInitiatingPayment, setIsInitiatingPayment] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentToken, setPaymentToken] = useState<string | null>(null);
  const [selectedPlanForPayment, setSelectedPlanForPayment] = useState<Plan | null>(null);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    const loadPlans = async () => {
      try {
        const response = await getPlans();
        setPlans(response.plans);
      } catch (error) {
        console.error('Failed to load plans:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPlans();
  }, []);

  const moduleNames = {
    mail: 'E-Posta Modülü',
    template: 'Şablon Modülü',
    bundle: 'Avukasa Ofis',
  };

  const moduleIcons = {
    mail: Mail,
    template: FileText,
    bundle: Layers,
  };

  const tierNames = {
    standard: 'Standart',
    pro: 'Profesyonel',
    unlimited: 'Sınırsız',
  };

  const handleSelectPlan = async (plan: Plan) => {
    try {
      setIsInitiatingPayment(true);
      setError(null);
      setSelectedPlanForPayment(plan);

      const request: InitiatePaymentRequest = {
        module_type: plan.module,
        tier: plan.tier,
        billing_cycle: billingCycle,
      };

      const response = await initiatePayment(request);
      setPaymentToken(response.iframe_token);
    } catch (err: any) {
      const message = err.response?.data?.error?.message || 'Ödeme başlatılamadı';
      setError(message);
      console.error('Failed to initiate payment:', err);
      setSelectedPlanForPayment(null);
    } finally {
      setIsInitiatingPayment(false);
    }
  };

  const handlePaymentSuccess = async () => {
    // User state'i refresh et (subscription plan güncellenecek)
    await refreshAuth();
    // Modal'ı kapat ve kullanıcıyı dashboard'da bırak
    setPaymentToken(null);
    setSelectedPlanForPayment(null);
  };

  const handlePaymentError = (message: string) => {
    setError(message);
    setPaymentToken(null);
    setSelectedPlanForPayment(null);
  };

  const handlePaymentClose = () => {
    setPaymentToken(null);
    setSelectedPlanForPayment(null);
  };

  if (loading || !plans) {
    return (
      <div className="flex flex-col items-center justify-center p-12 bg-white rounded-2xl border border-gray-200 min-h-[60vh]">
        <div className="animate-pulse space-y-4 w-full max-w-6xl">
          <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
          <div className="grid md:grid-cols-3 gap-6 mt-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-96 bg-gray-100 rounded-2xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const displayPlans = plans[selectedModule] || [];

  // Ödeme modal'ı göster
  if (paymentToken && selectedPlanForPayment) {
    const price = billingCycle === 'yearly'
      ? selectedPlanForPayment.yearly_price
      : selectedPlanForPayment.monthly_price;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
            <h2 className="text-2xl font-bold text-gray-900">Ödeme Bilgileri</h2>
            <button
              onClick={handlePaymentClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="p-6">
            <div className="mb-6 bg-gray-50 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-gray-900">{selectedPlanForPayment.name}</p>
                  <p className="text-sm text-gray-600">{tierNames[selectedPlanForPayment.tier]}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900">{formatPrice(price)}</p>
                  <p className="text-sm text-gray-500">/ay</p>
                </div>
              </div>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <PayTRIframe
              token={paymentToken}
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
              onClose={handlePaymentClose}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center p-8 bg-white rounded-2xl border border-gray-200 min-h-[60vh]">
      <div className="text-center max-w-2xl mx-auto mb-10">
        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 transform rotate-3 shadow-lg">
          <Sparkles className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-4">
          Bu Özelliğe Erişmek İçin Paketinizi Yükseltin
        </h2>
        <p className="text-lg text-gray-600">
          Bu özellik şu anda kullandığınız pakete dahil değildir.
          Tüm özellikleri keşfetmek için paketleri inceleyin.
        </p>
      </div>

      {/* Billing Cycle Toggle */}
      <div className="flex justify-center mb-6">
        <div className="bg-gray-100 p-1 rounded-full border border-gray-200 inline-flex relative items-center">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${billingCycle === 'monthly'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
              }`}
          >
            Aylık
          </button>
          <button
            onClick={() => setBillingCycle('yearly')}
            className={`px-6 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${billingCycle === 'yearly'
                ? 'bg-blue-500 text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
              }`}
          >
            Yıllık
            <span className="text-[10px] font-bold bg-yellow-400 text-gray-900 px-2 py-0.5 rounded-full">
              %20 İNDİRİM
            </span>
          </button>
        </div>
      </div>

      {/* Module Selection */}
      {!requiredPlan && (
        <div className="flex justify-center gap-4 mb-8">
          {(Object.keys(moduleNames) as Array<keyof typeof moduleNames>).map((module) => {
            const Icon = moduleIcons[module];
            return (
              <button
                key={module}
                onClick={() => setSelectedModule(module)}
                className={`px-6 py-3 rounded-xl font-medium transition-all flex items-center gap-2 ${selectedModule === module
                    ? 'bg-blue-50 text-blue-600 border-2 border-blue-500'
                    : 'bg-gray-50 text-gray-600 border-2 border-gray-200 hover:border-gray-300'
                  }`}
              >
                <Icon className="w-5 h-5" />
                {moduleNames[module]}
              </button>
            );
          })}
        </div>
      )}

      {/* Plans Grid */}
      <div className="grid md:grid-cols-3 gap-6 w-full max-w-6xl">
        {displayPlans.map((plan) => {
          const isPro = plan.tier === 'pro';
          const isUnlimited = plan.tier === 'unlimited';
          const price = billingCycle === 'yearly' ? plan.yearly_price : plan.monthly_price;

          return (
            <div
              key={plan.id}
              className={`relative rounded-2xl p-6 border-2 flex flex-col transition-all duration-300 ${isUnlimited
                  ? 'bg-gradient-to-b from-purple-600 to-purple-800 border-purple-500 text-white shadow-xl transform hover:-translate-y-1'
                  : isPro
                    ? 'bg-gradient-to-b from-blue-600 to-blue-800 border-blue-500 text-white shadow-xl transform hover:-translate-y-1'
                    : 'bg-white border-gray-200 hover:border-gray-300 text-gray-900 hover:shadow-lg'
                }`}
            >
              {/* Popular/Recommended Badge */}
              {isPro && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-yellow-400 text-gray-900 text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1 shadow-md">
                    <Sparkles className="w-3 h-3" />
                    EN POPÜLER
                  </span>
                </div>
              )}

              {isUnlimited && (
                <div className="absolute -top-3 right-0">
                  <span className="bg-purple-300 text-gray-900 text-xs font-bold px-3 py-1 rounded-bl-lg rounded-tr-lg">
                    KURUMSAL
                  </span>
                </div>
              )}

              {/* Plan Header */}
              <div className="mb-6">
                <h3 className="text-xl font-bold mb-2">{tierNames[plan.tier]}</h3>
                <p className={`text-sm ${isUnlimited || isPro ? 'text-white/80' : 'text-gray-600'}`}>
                  {plan.description}
                </p>
              </div>

              {/* Price */}
              <div className="mb-6">
                <span className="text-4xl font-bold tracking-tight">{formatPrice(price)}</span>
                <span className={`text-sm ml-2 ${isUnlimited || isPro ? 'text-white/70' : 'text-gray-500'}`}>
                  /ay
                </span>
                {billingCycle === 'yearly' && (
                  <p className="text-xs mt-1 text-gray-500">
                    Yıllık ödeme ile %20 tasarruf
                  </p>
                )}
              </div>

              {/* Features */}
              <div className="flex-grow mb-6">
                <ul className="space-y-3">
                  {plan.perks.map((perk, index) => (
                    <li key={index} className="flex items-start gap-3 text-sm">
                      <CheckCircle
                        className={`h-5 w-5 flex-shrink-0 mt-0.5 ${isUnlimited
                            ? 'text-purple-200'
                            : isPro
                              ? 'text-blue-200'
                              : 'text-green-500'
                          }`}
                      />
                      <span className={isUnlimited || isPro ? 'text-white/90' : 'text-gray-700'}>
                        {perk}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA Button */}
              <button
                onClick={() => handleSelectPlan(plan)}
                disabled={isInitiatingPayment}
                className={`w-full py-3 px-6 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${isUnlimited
                    ? 'bg-white text-purple-900 hover:bg-gray-100 shadow-lg'
                    : isPro
                      ? 'bg-white text-blue-900 hover:bg-gray-100 shadow-lg'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
              >
                {isInitiatingPayment ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Yükleniyor...
                  </>
                ) : (
                  <>
                    {isUnlimited ? 'Kurumsal Başla' : isPro ? 'Profesyonel Başla' : 'Hemen Başla'}
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          );
        })}
      </div>

      {/* Additional Info */}
      <div className="mt-8 text-center">
        <p className="text-sm text-gray-500">
          İstediğiniz zaman iptal edebilirsiniz.
        </p>
      </div>
    </div>
  );
};

export default UpgradePrompt;
